import type { ComponentType } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router';
import { AppShell } from '@/components/layout/app-shell';
import { BrandMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { useAuthQuery } from '@/hooks/auth';
import { FullPageLoader, GuestOnly, RequireAuth, RequireWorkspace } from './guards';

type Loader = () => Promise<Record<string, unknown>>;

function page(loader: Loader, name: string) {
  return {
    lazy: async () => {
      const module = await loader();
      return { Component: module[name] as ComponentType };
    },
  };
}

function RootLayout() {
  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  );
}

function IndexRedirect() {
  const auth = useAuthQuery();
  if (auth.isPending) return <FullPageLoader />;
  return <Navigate to={auth.data ? '/dashboard' : '/signin'} replace />;
}

function RouteError() {
  const error = useRouteError();
  const chunkFailed =
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(error.message);
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-canvas px-6 text-center">
      <BrandMark className="size-11" />
      <div>
        <h1 className="font-display text-xl font-semibold text-ink">
          {notFound ? 'Page not found' : chunkFailed ? 'A new version is available' : 'Something went wrong'}
        </h1>
        <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-muted">
          {chunkFailed
            ? 'easiibill was updated. Reload to continue with the latest version.'
            : notFound
              ? 'The page you are looking for does not exist.'
              : 'An unexpected error occurred while showing this page. Your data is safe.'}
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => (window.location.href = '/dashboard')}>
          Go to dashboard
        </Button>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    </div>
  );
}

const auth = () => import('@/features/auth/pages');

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      { index: true, element: <IndexRedirect /> },
      {
        path: 'invoice/public/:token',
        ...page(() => import('@/features/public/public-invoice'), 'PublicInvoicePage'),
      },
      {
        element: <GuestOnly />,
        children: [
          { path: 'signin', ...page(auth, 'SignInPage') },
          { path: 'signup', ...page(auth, 'SignUpPage') },
          { path: 'forgot-password', ...page(auth, 'ForgotPasswordPage') },
        ],
      },
      { path: 'check-email', ...page(auth, 'CheckEmailPage') },
      { path: 'verify-email', ...page(auth, 'VerifyEmailPage') },
      { path: 'reset-password', ...page(auth, 'ResetPasswordPage') },
      {
        element: <RequireAuth />,
        children: [
          { path: 'onboarding', ...page(() => import('@/features/onboarding/onboarding'), 'OnboardingPage') },
          {
            element: <RequireWorkspace />,
            children: [
              {
                path: 'templates/:id/studio',
                ...page(() => import('@/features/studio/studio'), 'StudioPage'),
              },
              {
                element: <AppShell />,
                children: [
                  {
                    path: 'dashboard',
                    ...page(() => import('@/features/dashboard/dashboard'), 'DashboardPage'),
                  },
                  { path: 'invoices', ...page(() => import('@/features/invoices/list'), 'InvoiceListPage') },
                  {
                    path: 'invoices/new',
                    ...page(() => import('@/features/invoices/editor'), 'InvoiceEditorPage'),
                  },
                  {
                    path: 'invoices/:id',
                    ...page(() => import('@/features/invoices/detail'), 'InvoiceDetailPage'),
                  },
                  {
                    path: 'invoices/:id/edit',
                    ...page(() => import('@/features/invoices/editor'), 'InvoiceEditorPage'),
                  },
                  {
                    path: 'recurring',
                    ...page(() => import('@/features/recurring/list'), 'RecurringListPage'),
                  },
                  {
                    path: 'recurring/new',
                    ...page(() => import('@/features/recurring/editor'), 'RecurringEditorPage'),
                  },
                  {
                    path: 'recurring/:id',
                    ...page(() => import('@/features/recurring/editor'), 'RecurringEditorPage'),
                  },
                  {
                    path: 'customers',
                    ...page(() => import('@/features/customers/list'), 'CustomerListPage'),
                  },
                  {
                    path: 'customers/:id',
                    ...page(() => import('@/features/customers/detail'), 'CustomerDetailPage'),
                  },
                  { path: 'products', ...page(() => import('@/features/products/list'), 'ProductListPage') },
                  { path: 'payments', ...page(() => import('@/features/payments/list'), 'PaymentListPage') },
                  {
                    path: 'templates',
                    ...page(() => import('@/features/templates/gallery'), 'TemplateGalleryPage'),
                  },
                  { path: 'reports', ...page(() => import('@/features/reports/reports'), 'ReportsPage') },
                  { path: 'settings', ...page(() => import('@/features/settings/settings'), 'SettingsPage') },
                  {
                    path: 'settings/:tab',
                    ...page(() => import('@/features/settings/settings'), 'SettingsPage'),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', ...page(() => import('@/features/errors/not-found'), 'NotFoundPage') },
    ],
  },
]);
